import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import SendIcon from "@mui/icons-material/Send";
import { LoadingButton } from "@mui/lab";
import { useCreateNewSecureMessageMutation, useFinanceSummaryQuery, useToDoItemTypesQuery, } from "generated/generated-models";
import { useEffect, useState } from "react";
import AddAttachment from "./AddAttachment";
import { CError } from "app/common/CError";
import { Loading } from "app/lib/components/Loading";
import { makeNonEmpty } from "core/util/array";
import { Form, Formik } from "formik";
import { Validate } from "./CreateNewMessageValidate";
import { nameof } from "core/util/nameof";
import FieldErrorMessage from "app/components/FieldErrorMessage";
import { FileThumb } from "app/disbursement/FileThumb";
import { FileUploader } from "./FileUploader";
import { Grid, FormControl, InputLabel, Select, MenuItem, OutlinedInput, Button, } from "@mui/material";
import { Guid } from "guid-typescript";
const CreateNewMessage = (props) => {
    const [toDoItemTypesArray, setToDoItemTypesArray] = useState([]);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [files, setFiles] = useState([]);
    const [fileData, setFileData] = useState([]);
    const [error, setError] = useState("");
    const { data: financeSummary, loading: loadingFinanceSummary, error: financeSummaryErrors, } = useFinanceSummaryQuery();
    const accountNumbers = financeSummary?.financeSummary?.Instruments?.flatMap((instrument) => instrument?.Accounts).flatMap((account) => account?.Number);
    useEffect(() => {
        async function validateTotalFileSize() {
            const error = await FileUploader.validateTotalFileSize(files.map((x) => x.file));
            setError(error || null);
        }
        validateTotalFileSize();
    }, [files]);
    const handleIsFileUploading = (loading) => {
        setIsFileUploading(loading);
    };
    const handleFileUpload = async (file, data) => {
        const error = await FileUploader.validateFile(file);
        if (error) {
            setError(error);
            return;
        }
        setFiles((prevFiles) => [...prevFiles, { file: file, id: Guid.raw() }]);
        setFileData((prevFileData) => [
            ...prevFileData,
            {
                fileName: file.name,
                fileSize: file.size,
                data: data,
            },
        ]);
        setError(null);
    };
    const handleRemoveFile = (file) => {
        setFiles((prevFiles) => prevFiles.filter((fileItem) => fileItem.file !== file));
        setFileData((prevFileData) => {
            const updatedFileData = Array.isArray(prevFileData)
                ? prevFileData.filter((fileItem) => fileItem.fileName !== file.name)
                : [];
            return updatedFileData;
        });
    };
    const { data: toDoItemTypes, loading: loadingToDoItemTypes, error: errorToDoItemTypes, } = useToDoItemTypesQuery({
        variables: { input: { IsSecureMessage: true } },
        fetchPolicy: "network-only",
    });
    useEffect(() => {
        if (!loadingToDoItemTypes && toDoItemTypes) {
            setToDoItemTypesArray(makeNonEmpty(toDoItemTypes.toDoItemTypes).sort((toDoItemTypeA, toDoItemTypeB) => !toDoItemTypeA.SecureMessageType ||
                !toDoItemTypeB.SecureMessageType ||
                toDoItemTypeA.SecureMessageType.DisplayOrder >
                    toDoItemTypeB.SecureMessageType.DisplayOrder
                ? 1
                : -1));
        }
    }, [toDoItemTypes, loadingToDoItemTypes]);
    function handleCancelClick() {
        props.handleCloseNewMessage();
    }
    const [createNewSecureMessage] = useCreateNewSecureMessageMutation();
    async function handleSubmit(values) {
        const result = await createNewSecureMessage({
            variables: {
                input: {
                    AccountNumber: values.SelectedToDoItem?.SecureMessageType
                        ?.IsAccount
                        ? values.SelectedAccount
                        : undefined,
                    Message: values.Message,
                    ToDoItemTypeId: values.SelectedToDoItem?.Id,
                    FileData: fileData || undefined,
                },
            },
        });
        if (result.data?.requestNewSecureMessage?.GraphSecureMessage != null) {
            props.handleCreatedNewMessage(result.data?.requestNewSecureMessage?.GraphSecureMessage);
            props.handleCloseNewMessage();
        }
    }
    if (loadingToDoItemTypes) {
        return _jsx(Loading, {});
    }
    if (errorToDoItemTypes) {
        return _jsx(CError, { error: "Could not retrieve subjects" });
    }
    const initialValues = {
        SelectedToDoItem: undefined,
        SelectedAccount: "",
        Message: "",
        FileData: [],
    };
    return (_jsx(Formik, { initialValues: initialValues, onSubmit: (values) => handleSubmit(values), validationSchema: Validate, children: ({ isSubmitting, values, handleChange, errors, touched }) => {
            return (_jsxs(Form, { children: [_jsx("h2", { children: "New Message" }), _jsxs(Grid, { container: true, spacing: 2, sx: { mt: 0, mb: 4 }, className: "createNewMessage", children: [_jsx(Grid, { item: true, xs: 12, children: _jsxs(FormControl, { fullWidth: true, variant: "outlined", error: !!(errors.SelectedToDoItem &&
                                        touched.SelectedToDoItem), children: [_jsx(InputLabel, { children: "Subject" }), _jsx(Select, { fullWidth: true, label: "Subject", onChange: handleChange, name: nameof("SelectedToDoItem"), children: toDoItemTypesArray.map((toDoItemType) => {
                                                return (_jsx(MenuItem, { value: 
                                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                    toDoItemType, children: toDoItemType.Description }, toDoItemType.Id));
                                            }) }), _jsx(FieldErrorMessage, { errorName: nameof("SelectedToDoItem") })] }) }), values.SelectedToDoItem?.SecureMessageType
                                ?.IsAccount && (_jsxs(Grid, { item: true, xs: 12, children: [loadingFinanceSummary && _jsx(Loading, {}), financeSummaryErrors && (_jsx(CError, { error: "Could not retrieve accounts" })), !loadingFinanceSummary &&
                                        !financeSummaryErrors && (_jsxs(FormControl, { fullWidth: true, variant: "outlined", error: !!(errors.SelectedAccount &&
                                            touched.SelectedAccount), children: [_jsx(InputLabel, { children: "Account" }), _jsx(Select, { fullWidth: true, label: "Account", onChange: handleChange, name: nameof("SelectedAccount"), value: values.SelectedAccount, children: accountNumbers?.map((accountNumber) => {
                                                    return (_jsx(MenuItem, { value: accountNumber, children: accountNumber }, accountNumber));
                                                }) }), _jsx(FieldErrorMessage, { errorName: nameof("SelectedAccount") })] }))] })), _jsx(Grid, { item: true, xs: 12, children: _jsxs(FormControl, { fullWidth: true, variant: "outlined", error: !!(errors.Message && touched.Message), children: [_jsx(InputLabel, { htmlFor: "message", error: true, children: "Message" }), _jsx(OutlinedInput, { id: "message", type: "text", label: "Message", multiline: true, minRows: 5, onChange: handleChange, name: nameof("Message") }), _jsx(FieldErrorMessage, { errorName: nameof("Message") })] }) }), _jsx(AddAttachment, { onFileUpload: handleFileUpload, isFileUploading: handleIsFileUploading })] }), _jsxs(Grid, { item: true, xs: 6, children: [error && _jsx(CError, { error: error }), files.map((file) => (_jsx(FileThumb, { file: file.file, onRemove: () => handleRemoveFile(file.file) }, file.id)))] }), _jsxs("div", { className: "button-row", children: [_jsx(Button, { className: "btn-cancel", id: "cancel", sx: { p: 1.5, mt: 1 }, variant: "contained", onClick: handleCancelClick, children: "Cancel" }), _jsx(LoadingButton, { className: "btn-sendMessage", type: "submit", id: "submit", loading: isSubmitting, sx: { p: 1.5, mt: 1 }, loadingPosition: "end", variant: "contained", startIcon: _jsx(SendIcon, {}), disabled: isFileUploading || error !== null, children: "Send Message" })] })] }));
        } }));
};
export default CreateNewMessage;
